import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input } from 'reactstrap';

interface UpdatesRequiredModalProps {
  isOpen: boolean;
  toggle: () => void;
  onConfirm: (reason: string) => void; // Fixed: Made required and added reason parameter
}

const reasonOptions = [
  'Inspection Incomplete.',
  'Insufficient data provided in the Report.',
  'Additional Information required on Inspection Report.',
  'Some sections require additional information or clarification.',
  'Others',
];

const UpdatesRequiredModal: React.FC<UpdatesRequiredModalProps> = ({
  isOpen,
  toggle,
  onConfirm
}) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');

  const maxCustomReasonLength = 250;

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedReason('');
      setCustomReason('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    let finalReason = '';

    if (selectedReason === 'Others') {
      finalReason = customReason.trim();
    } else {
      finalReason = selectedReason;
    }

    if (finalReason) {
      // Call onConfirm with the reason - parent will handle API call
      onConfirm(finalReason);
      // Don't call toggle() here - let parent handle modal closing after API call
    }
  };

  const isFormValid = () => {
    if (!selectedReason) return false;
    if (selectedReason === 'Others') {
      return customReason.trim().length > 0 && customReason.trim().length <= maxCustomReasonLength;
    }
    return true;
  };

  return (
      <Modal isOpen={isOpen} toggle={toggle} centered size="md">
        <ModalHeader toggle={toggle} className="fw-bold" style={{ color: 'black' }}>
          Updates required
        </ModalHeader>
        <ModalBody className="pt-0 pb-2 px-3 px-md-4">
          <hr className="mt-3"/>
          
          <FormGroup className="mb-4">
            <Label className="fw-bold mb-2">Select Reason</Label>
            <Input
              type="select"
              value={selectedReason}
              onChange={e => setSelectedReason(e.target.value)}
              style={{ height: 48, fontSize: 16 }}
            >
              <option value="" disabled hidden>-- Select a reason --</option>
              {reasonOptions.map((reason, idx) => (
                <option key={idx} value={reason}>{reason}</option>
              ))}
            </Input>
          </FormGroup>
  
          {/* Show custom reason textarea only when "Others" is selected */}
          {selectedReason === 'Others' && (
            <FormGroup className="mb-3">
              <Label className="fw-bold mb-2">
                Write Custom Reason
                <span className="text-muted ms-2">({customReason.length}/{maxCustomReasonLength})</span>
              </Label>
              <Input
                type="textarea"
                rows={5}
                placeholder="Write your custom reason here..."
                value={customReason}
                onChange={e => {
                  if (e.target.value.length <= maxCustomReasonLength) {
                    setCustomReason(e.target.value);
                  }
                }}
                style={{ fontSize: 16, resize: 'none' }}
                className={customReason.length > maxCustomReasonLength ? 'is-invalid' : ''}
              />
              {customReason.length > maxCustomReasonLength && (
                <div className="invalid-feedback">
                  Maximum {maxCustomReasonLength} characters allowed
                </div>
              )}
            </FormGroup>
          )}
        </ModalBody>
        
        <ModalFooter className="justify-content-end gap-2">
          <Button 
            color="danger" 
            onClick={toggle}
            style={{ minWidth: '100px' }}
          >
            Cancel
          </Button>
          <Button 
            color="success" 
            onClick={handleConfirm}
            disabled={!isFormValid()}
            style={{ minWidth: '100px' }}
          >
            Request Revision
          </Button>
        </ModalFooter>
      </Modal>
    );
};

export default UpdatesRequiredModal;